import type { BudgetVsActualRow } from "../api/forecast_io_api"

/**
 * Budget vs actual, per customer (item 24): variance in dollars and percent, and — where the
 * SKUs behind the rows carry a catalog COGS — cost of goods and gross margin. The server hands
 * back raw customer × period sums (`BudgetVsActualRow`); everything derived is computed here in
 * cents so the panel never shows a floating-point tail.
 */

export interface CustomerVariance {
  customer: string
  budget_dollars: number
  actual_dollars: number
  /** actual − budget. Positive means the customer beat budget. */
  variance_dollars: number
  /** variance / budget × 100; null when there is no budget to compare against. */
  variance_pct: number | null
  budget_cases: number
  actual_cases: number
  variance_cases: number
  /** Null when no SKU under the customer carries a COGS. */
  budget_cogs: number | null
  actual_cogs: number | null
  budget_margin_dollars: number | null
  actual_margin_dollars: number | null
  /** margin / revenue × 100; null without COGS or without revenue. */
  budget_margin_pct: number | null
  actual_margin_pct: number | null
}

export interface BudgetVsActualSummary {
  rows: CustomerVariance[]
  total: CustomerVariance
  /** Periods the comparison covers, ascending. */
  periods: string[]
}

function to_cents(value: string | number | null | undefined): number {
  if (value == null || value === "") return 0
  const parsed = typeof value === "number" ? value : Number(value)
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
}

function from_cents(cents: number): number {
  return cents / 100
}

function pct(numerator_cents: number, denominator_cents: number): number | null {
  if (denominator_cents === 0) return null
  return Math.round((numerator_cents / denominator_cents) * 1000) / 10
}

interface Accumulator {
  customer: string
  budget: number
  actual: number
  budget_cases: number
  actual_cases: number
  budget_cogs: number | null
  actual_cogs: number | null
}

function empty(customer: string): Accumulator {
  return { customer, budget: 0, actual: 0, budget_cases: 0, actual_cases: 0, budget_cogs: null, actual_cogs: null }
}

function add_cogs(current: number | null, value: string | null): number | null {
  if (value == null) return current
  return (current ?? 0) + to_cents(value)
}

function finish(acc: Accumulator): CustomerVariance {
  const margin = (revenue: number, cogs: number | null): number | null => (cogs == null ? null : revenue - cogs)
  const budget_margin = margin(acc.budget, acc.budget_cogs)
  const actual_margin = margin(acc.actual, acc.actual_cogs)
  return {
    customer: acc.customer,
    budget_dollars: from_cents(acc.budget),
    actual_dollars: from_cents(acc.actual),
    variance_dollars: from_cents(acc.actual - acc.budget),
    variance_pct: pct(acc.actual - acc.budget, acc.budget),
    budget_cases: acc.budget_cases,
    actual_cases: acc.actual_cases,
    variance_cases: acc.actual_cases - acc.budget_cases,
    budget_cogs: acc.budget_cogs == null ? null : from_cents(acc.budget_cogs),
    actual_cogs: acc.actual_cogs == null ? null : from_cents(acc.actual_cogs),
    budget_margin_dollars: budget_margin == null ? null : from_cents(budget_margin),
    actual_margin_dollars: actual_margin == null ? null : from_cents(actual_margin),
    budget_margin_pct: budget_margin == null ? null : pct(budget_margin, acc.budget),
    actual_margin_pct: actual_margin == null ? null : pct(actual_margin, acc.actual),
  }
}

/**
 * Roll the server's customer × period rows up to one line per customer plus a total, sorted by
 * actual dollars descending. `periods` narrows the comparison to those periods when given.
 */
export function summarize_budget_vs_actual(rows: BudgetVsActualRow[], periods?: string[]): BudgetVsActualSummary {
  const wanted = periods && periods.length > 0 ? new Set(periods) : null
  const by_customer = new Map<string, Accumulator>()
  const total = empty("All customers")
  const seen_periods = new Set<string>()
  for (const row of rows) {
    if (wanted && !wanted.has(row.period)) continue
    seen_periods.add(row.period)
    const acc = by_customer.get(row.customer) ?? empty(row.customer)
    for (const target of [acc, total]) {
      target.budget += to_cents(row.budget_dollars)
      target.actual += to_cents(row.actual_dollars)
      target.budget_cases += Number(row.budget_cases) || 0
      target.actual_cases += Number(row.actual_cases) || 0
      target.budget_cogs = add_cogs(target.budget_cogs, row.budget_cogs)
      target.actual_cogs = add_cogs(target.actual_cogs, row.actual_cogs)
    }
    by_customer.set(row.customer, acc)
  }
  const result = Array.from(by_customer.values()).map(finish)
  result.sort((a: CustomerVariance, b: CustomerVariance) => b.actual_dollars - a.actual_dollars || a.customer.localeCompare(b.customer))
  return { rows: result, total: finish(total), periods: Array.from(seen_periods).sort() }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function row(customer: string, period: string, budget: string, actual: string, extra: Partial<BudgetVsActualRow> = {}): BudgetVsActualRow {
    return {
      customer,
      period,
      budget_dollars: budget,
      actual_dollars: actual,
      budget_cases: "0",
      actual_cases: "0",
      budget_cogs: null,
      actual_cogs: null,
      ...extra,
    }
  }

  describe("summarize_budget_vs_actual", () => {
    it("sums periods per customer and reports variance in dollars and percent", () => {
      const summary = summarize_budget_vs_actual([
        row("Hy-Vee", "2026-01", "1000.00", "1100.00", { budget_cases: "10", actual_cases: "11" }),
        row("Hy-Vee", "2026-02", "1000.10", "900.05", { budget_cases: "10", actual_cases: "9" }),
        row("Kroger", "2026-01", "500.00", "500.00"),
      ])
      expect(summary.periods).toEqual(["2026-01", "2026-02"])
      const hyvee = summary.rows.find((r: CustomerVariance) => r.customer === "Hy-Vee")!
      expect(hyvee.budget_dollars).toBe(2000.1)
      expect(hyvee.actual_dollars).toBe(2000.05)
      expect(hyvee.variance_dollars).toBe(-0.05)
      expect(hyvee.variance_pct).toBe(-0)
      expect(hyvee.variance_cases).toBe(0)
      const kroger = summary.rows.find((r: CustomerVariance) => r.customer === "Kroger")!
      expect(kroger.variance_pct).toBe(0)
      expect(summary.total.budget_dollars).toBe(2500.1)
      expect(summary.total.variance_dollars).toBe(-0.05)
    })

    it("orders customers by actual dollars and keeps the percent null without a budget", () => {
      const summary = summarize_budget_vs_actual([
        row("Small", "2026-01", "0", "100.00"),
        row("Big", "2026-01", "1000.00", "1500.00"),
      ])
      expect(summary.rows.map((r: CustomerVariance) => r.customer)).toEqual(["Big", "Small"])
      expect(summary.rows[1].variance_pct).toBeNull()
      expect(summary.rows[0].variance_pct).toBe(50)
    })

    it("computes COGS and margin only where the catalog priced the SKUs", () => {
      const summary = summarize_budget_vs_actual([
        row("Hy-Vee", "2026-01", "1000.00", "1200.00", { budget_cogs: "400.00", actual_cogs: "480.00" }),
        row("Kroger", "2026-01", "500.00", "400.00"),
      ])
      const hyvee = summary.rows.find((r: CustomerVariance) => r.customer === "Hy-Vee")!
      expect(hyvee.actual_cogs).toBe(480)
      expect(hyvee.actual_margin_dollars).toBe(720)
      expect(hyvee.actual_margin_pct).toBe(60)
      expect(hyvee.budget_margin_pct).toBe(60)
      const kroger = summary.rows.find((r: CustomerVariance) => r.customer === "Kroger")!
      expect(kroger.actual_cogs).toBeNull()
      expect(kroger.actual_margin_dollars).toBeNull()
      expect(kroger.actual_margin_pct).toBeNull()
      // The total carries only the priced part, never a margin on unpriced revenue.
      expect(summary.total.actual_cogs).toBe(480)
      expect(summary.total.actual_margin_dollars).toBe(1600 - 480)
    })

    it("narrows to the periods asked for and survives an empty input", () => {
      const summary = summarize_budget_vs_actual(
        [row("Hy-Vee", "2026-01", "100", "90"), row("Hy-Vee", "2026-02", "100", "120")],
        ["2026-02"]
      )
      expect(summary.periods).toEqual(["2026-02"])
      expect(summary.rows[0].variance_dollars).toBe(20)
      const nothing = summarize_budget_vs_actual([])
      expect(nothing.rows).toEqual([])
      expect(nothing.total.variance_pct).toBeNull()
    })
  })
}
